import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, Pencil, Plus, Trash2 } from "lucide-react";
import {
  adminLogin,
  deleteAdminItem,
  getAdminEnquiries,
  getAdminItems,
  getAdminOrders,
  syncShipmondo,
  updateAdminItem,
  formatDkk,
} from "@/lib/site";
import { ItemEditor } from "@/components/admin/ItemEditor";
import { EventsAdmin, ForsideAdmin, LineageAdmin, ReviewsAdmin, TrafficAdmin } from "@/components/admin/ExtrasAdmin";

const TABS = [
  ["orders", "Ordrer"],
  ["enquiries", "Forespørgsler"],
  ["traffic", "Trafik"],
  ["products", "Produkter"],
  ["geckos", "Gekkoer"],
  ["terrariums", "Terrarier"],
  ["events", "Messer"],
  ["lineage", "Linjeavl"],
  ["reviews", "Anmeldelser"],
  ["forside", "Forside"],
];

const GECKO_STATUS = ["Tilgængelig", "Planlagt 2027", "Udsolgt"];

function OrderStatus({ status, payment }) {
  const paid = payment === "paid";
  return (
    <span
      className={`border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${
        paid ? "border-forest/50 bg-forest/15 text-pale" : "border-white/15 text-sage"
      }`}
    >
      {paid ? "Betalt" : status}
    </span>
  );
}

function ItemRow({ coll, item, onSaved, onEdit }) {
  const [price, setPrice] = useState(item.price_dkk);
  const [status, setStatus] = useState(item.status || "");
  const [weight, setWeight] = useState(item.weight_kg ?? 0);
  const [stock, setStock] = useState(item.inventory_qty ?? 0);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { price_dkk: Number(price), inventory_qty: Number(stock) };
      if (coll === "geckos" || coll === "terrariums") payload.status = status;
      if (coll === "products") payload.weight_kg = Number(weight);
      await updateAdminItem(coll, item.slug, payload);
      toast.success(`${item.name} gemt`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke gemme");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Slet "${item.name}" permanent? Dette kan ikke fortrydes.`)) return;
    setDeleting(true);
    try {
      await deleteAdminItem(coll, item.slug);
      toast.success(`${item.name} slettet`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke slette");
      setDeleting(false);
    }
  };

  return (
    <div
      data-testid={`admin-item-${item.slug}`}
      className="flex flex-wrap items-center gap-4 border-b border-white/10 py-4"
    >
      {item.image && (
        <img src={item.image} alt="" className="h-12 w-12 border border-white/10 object-cover" />
      )}
      <p className="min-w-0 flex-1 truncate text-sm text-cream">{item.name}</p>
      <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-sage">
        Pris
        <input
          data-testid={`admin-price-${item.slug}`}
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          className="w-24 border border-white/15 bg-ink px-2 py-1.5 text-sm text-cream outline-none focus:border-forest"
        />
      </label>
      {coll === "products" && (
        <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-sage">
          Kg
          <input
            data-testid={`admin-weight-${item.slug}`}
            type="number"
            step="0.1"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            className="w-20 border border-white/15 bg-ink px-2 py-1.5 text-sm text-cream outline-none focus:border-forest"
          />
        </label>
      )}
      <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-sage">
        Lager
        <input
          data-testid={`admin-stock-${item.slug}`}
          type="number"
          value={stock}
          onChange={(e) => setStock(e.target.value)}
          className="w-20 border border-white/15 bg-ink px-2 py-1.5 text-sm text-cream outline-none focus:border-forest"
        />
      </label>
      {(coll === "geckos" || coll === "terrariums") && (
        <select
          data-testid={`admin-status-${item.slug}`}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="border border-white/15 bg-ink px-2 py-1.5 text-sm text-cream outline-none focus:border-forest"
        >
          {(coll === "geckos" ? GECKO_STATUS : [item.status]).map((s) => (
            <option key={s} value={s} className="bg-ink">
              {s}
            </option>
          ))}
        </select>
      )}
      <button
        data-testid={`admin-save-${item.slug}`}
        onClick={save}
        disabled={saving}
        className="rounded-full bg-forest px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pale hover:text-ink disabled:opacity-50"
      >
        {saving ? "…" : "Gem"}
      </button>
      <button
        data-testid={`admin-edit-${item.slug}`}
        onClick={() => onEdit(item)}
        className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-sage transition-colors hover:border-white/40 hover:text-cream"
      >
        <Pencil className="h-3 w-3" /> Rediger
      </button>
      <button
        data-testid={`admin-delete-${item.slug}`}
        onClick={remove}
        disabled={deleting}
        className="flex items-center gap-1.5 rounded-full border border-red-900/50 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-red-400/80 transition-colors hover:border-red-500 hover:text-red-300 disabled:opacity-50"
      >
        <Trash2 className="h-3 w-3" /> {deleting ? "…" : "Slet"}
      </button>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("gf_admin") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [items, setItems] = useState({ geckos: [], products: [], terrariums: [] });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // { coll, item | null }

  const loadAll = async (t = token) => {
    if (!t) return;
    setLoading(true);
    try {
      const [o, e, i] = await Promise.all([
        getAdminOrders(t),
        getAdminEnquiries(t),
        getAdminItems(t),
      ]);
      setOrders(o);
      setEnquiries(e);
      setItems(i);
    } catch {
      localStorage.removeItem("gf_admin");
      setToken("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await adminLogin(email, password);
      localStorage.setItem("gf_admin", res.token);
      setToken(res.token);
      await loadAll(res.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login fejlede");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("gf_admin");
    setToken("");
  };

  const field =
    "w-full border border-white/15 bg-ink px-4 py-3 text-sm text-cream placeholder:text-sage/50 outline-none transition-colors duration-300 focus:border-forest";

  if (!token) {
    return (
      <div className="flex min-h-[80vh] items-center justify-center px-6 pb-24 pt-32">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-sm space-y-5 border border-white/10 bg-moss p-8"
        >
          <div className="flex items-center gap-3">
            <img
              src="/assets/logo-icon.png"
              alt=""
              className="h-10 w-10 rounded-full border border-white/15 object-cover"
            />
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-pale">
              GEKKOFUEL Admin
            </p>
          </div>
          <input
            data-testid="admin-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="E-mail"
            className={field}
          />
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Adgangskode"
            className={field}
          />
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loggingIn}
            className="flex w-full items-center justify-center gap-3 rounded-full bg-forest py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pale hover:text-ink disabled:opacity-60"
          >
            {loggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log ind"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="px-6 pb-24 pt-32 lg:px-16">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-serif text-4xl tracking-tight text-cream">Admin</h1>
        <button
          data-testid="admin-logout-button"
          onClick={logout}
          className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-sage transition-colors hover:text-cream"
        >
          <LogOut className="h-4 w-4" /> Log ud
        </button>
      </div>

      <div className="mt-10 flex flex-wrap gap-2 border-b border-white/10 pb-4">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            data-testid={`admin-tab-${key}`}
            onClick={() => setTab(key)}
            className={`rounded-full border px-5 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
              tab === key
                ? "border-forest bg-forest text-cream"
                : "border-white/15 text-sage hover:border-white/40 hover:text-cream"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && <p className="mt-10 font-mono text-xs text-sage">Henter…</p>}

      {!loading && tab === "orders" && (
        <div data-testid="admin-orders" className="mt-8">
          <div className="mb-6 flex items-center gap-4">
            <button
              data-testid="admin-sync-tracking"
              onClick={async () => {
                try {
                  const r = await syncShipmondo();
                  toast.success(
                    r.tracking_emails_sent > 0
                      ? `${r.tracking_emails_sent} tracking-mail(s) sendt`
                      : "Ingen nye forsendelser at spore"
                  );
                  loadAll();
                } catch {
                  toast.error("Synkronisering fejlede");
                }
              }}
              className="rounded-full border border-white/15 px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-sage transition-colors hover:border-forest hover:text-cream"
            >
              Synk tracking fra Shipmondo
            </button>
            <p className="font-mono text-[10px] text-sage/60">
              Kører også automatisk hver 30. minut
            </p>
          </div>
          {orders.length === 0 && <p className="text-sm text-sage">Ingen ordrer endnu.</p>}
          {orders.map((o) => (
            <div
              key={o.session_id}
              className="flex flex-wrap items-center gap-4 border-b border-white/10 py-4 text-sm"
            >
              <span className="font-mono text-xs text-sage">
                {(o.created_at || "").slice(0, 10)}
              </span>
              <span className="text-cream">{o.customer?.name}</span>
              <span className="text-sage">{o.customer?.email}</span>
              <span className="font-mono text-xs text-pale">{formatDkk(o.amount_dkk)}</span>
              <span className="w-full text-xs text-sage" data-testid={`admin-order-contact-${o.session_id}`}>
                {o.customer?.phone && <span className="text-cream/80">Tlf: {o.customer.phone}</span>}
                {o.customer?.address && (
                  <span className="text-cream/80">
                    {o.customer?.phone ? " · " : ""}Adresse: {o.customer.address}
                    {o.postal_code || o.customer?.city
                      ? `, ${[o.postal_code, o.customer?.city].filter(Boolean).join(" ")}`
                      : ""}
                  </span>
                )}
                {!o.customer?.phone && !o.customer?.address && (
                  <span className="text-sage/60">Ingen telefon/adresse registreret (ældre ordre)</span>
                )}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-sage">
                {o.shipping}
                {o.pickup_address ? ` · ${o.pickup_address}` : ""}
                {o.event_name ? ` · ${o.event_name}` : ""}
                {o.postal_code ? ` · ${o.postal_code}` : ""}
              </span>
              <span className="text-xs text-sage">
                {(o.items || []).map((i) => `${i.qty}× ${i.name}`).join(", ")}
              </span>
              <span className="ml-auto flex items-center gap-2">
                {o.shipmondo_id && o.shipmondo_id !== "processing" && (
                  <span
                    data-testid={`admin-shipmondo-${o.session_id}`}
                    className="border border-pale/30 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-pale"
                  >
                    Shipmondo #{o.shipmondo_id}
                  </span>
                )}
                {o.shipmondo_error && (
                  <span className="border border-red-900/50 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-red-300">
                    Fragt fejlet
                  </span>
                )}
                <OrderStatus status={o.status} payment={o.payment_status} />
              </span>
            </div>
          ))}
        </div>
      )}

      {!loading && tab === "enquiries" && (
        <div data-testid="admin-enquiries" className="mt-8">
          {enquiries.length === 0 && <p className="text-sm text-sage">Ingen forespørgsler endnu.</p>}
          {enquiries.map((e, i) => (
            <div key={i} className="border-b border-white/10 py-4 text-sm">
              <p className="text-cream">
                {e.name} <span className="text-sage">· {e.email}</span>
              </p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-pale/70">
                {e.topic} · {(e.created_at || "").slice(0, 10)}
              </p>
              <p className="mt-2 text-sage">{e.message}</p>
            </div>
          ))}
        </div>
      )}

      {!loading && ["products", "geckos", "terrariums"].includes(tab) && (
        <div data-testid={`admin-items-${tab}`} className="mt-8">
          <button
            data-testid={`admin-new-${tab}`}
            onClick={() => setEditing({ coll: tab, item: null })}
            className="mb-6 flex items-center gap-2 rounded-full bg-forest px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pale hover:text-ink"
          >
            <Plus className="h-4 w-4" /> Ny vare
          </button>
          {items[tab].map((item) => (
            <ItemRow
              key={item.slug}
              coll={tab}
              item={item}
              onSaved={loadAll}
              onEdit={(it) => setEditing({ coll: tab, item: it })}
            />
          ))}
        </div>
      )}

      {!loading && tab === "events" && <EventsAdmin />}

      {!loading && tab === "lineage" && <LineageAdmin />}

      {!loading && tab === "reviews" && <ReviewsAdmin />}

      {!loading && tab === "forside" && <ForsideAdmin />}

      {!loading && tab === "traffic" && <TrafficAdmin />}

      {editing && (
        <ItemEditor
          collection={editing.coll}
          item={editing.item}
          onClose={() => setEditing(null)}
          onSaved={loadAll}
        />
      )}
    </div>
  );
}
